import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { resetPassword, requestPasswordReset } from '../services/authService';
import { validatePassword } from '../utils/validation';
import {
  AuthShell,
  AuthIconBadge,
  AuthField,
  KeyBadgeIcon,
  ShieldLockIcon,
  KeyIcon,
  LockIcon,
  EyeIcon,
  ArrowRightIcon,
  BackArrowIcon,
  SendIcon
} from './authShared';

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Resend code state & 60s cooldown timer
  const [showResendInput, setShowResendInput] = useState(false);
  const [resendEmail, setResendEmail] = useState(location.state?.email || '');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [resendError, setResendError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setInterval(() => {
        setResendCountdown(prev => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCountdown]);

  function clearFieldError(key) {
    if (fieldErrors[key]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    const newFieldErrors = {};

    const cleanToken = token.trim();
    if (!cleanToken) {
      newFieldErrors.token = 'Reset code is required.';
    } else if (!/^[0-9]{6}$/.test(cleanToken)) {
      newFieldErrors.token = 'Reset code must be a 6-digit token.';
    }

    const validation = validatePassword(password);
    if (!validation.ok) {
      newFieldErrors.password = validation.message;
    }

    if (!confirmPassword) {
      newFieldErrors.confirmPassword = 'Please confirm your new password.';
    } else if (password !== confirmPassword) {
      newFieldErrors.confirmPassword = 'Passwords do not match.';
    }

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError('');
      return;
    }

    setFieldErrors({});
    setLoading(true);
    try {
      const res = await resetPassword(cleanToken, password);
      if (res.data.success) {
        setSuccess(true);
      } else {
        const err = res.data.error || 'Unable to reset password.';
        if (err.toLowerCase().includes('token') || err.toLowerCase().includes('code')) {
          setFieldErrors({ token: err });
          setError('');
        } else {
          setError(err);
        }
      }
    } catch (err) {
      const serverErr = err.response?.data?.error || 'Unable to reset password.';
      if (serverErr.toLowerCase().includes('token') || serverErr.toLowerCase().includes('code')) {
        setFieldErrors({ token: serverErr });
        setError('');
      } else {
        setError(serverErr);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResendCode(e) {
    if (e) e.preventDefault();
    if (resending || resendCountdown > 0) return;

    const target = resendEmail.trim();
    if (!target) {
      setResendError('Please enter your email or username to receive a code.');
      return;
    }

    setResending(true);
    setResendError('');
    setResendMsg('');

    try {
      await requestPasswordReset(target);
      setResendMsg(`If that account exists, a new reset code has been sent.`);
      setResendCountdown(60);
    } catch (err) {
      // Backend failure: NO cooldown, allowing immediate retry
      setResendCountdown(0);
      setResendError(err.response?.data?.error || err.message || 'Failed to send reset code. Please try again.');
    } finally {
      setResending(false);
    }
  }

  if (success) {
    return (
      <AuthShell>
        <div className="auth-card">
          <AuthIconBadge><ShieldLockIcon /></AuthIconBadge>
          <h1 className="auth-card-title">Password updated</h1>
          <p className="auth-card-subtitle">Your password has been changed. You can now sign in with your new password.</p>
          <button className="auth-submit-btn" type="button" onClick={() => navigate('/login')}>
            Go to sign in <ArrowRightIcon />
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="auth-card">
        <AuthIconBadge><KeyBadgeIcon /></AuthIconBadge>
        <h1 className="auth-card-title">Reset your password</h1>
        <p className="auth-card-subtitle">Paste the 6-digit reset code we emailed you, then choose a new password.</p>

        <form onSubmit={submit} noValidate>
          {error && <div className="auth-alert">{error}</div>}
          {resendMsg && <div className="auth-alert auth-alert-success">{resendMsg}</div>}

          <AuthField
            label="Reset code"
            icon={<KeyIcon />}
            placeholder="000000"
            maxLength={6}
            value={token}
            onChange={e => {
              setToken(e.target.value.replace(/\D/g, ''));
              clearFieldError('token');
            }}
            error={fieldErrors.token}
            required
            autoFocus
          />

          <AuthField
            label="New password"
            icon={<LockIcon />}
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your new password"
            value={password}
            onChange={e => {
              setPassword(e.target.value);
              clearFieldError('password');
            }}
            error={fieldErrors.password}
            required
            suffix={(
              <button
                type="button"
                className="auth-eye-btn"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide entered value' : 'Show entered value'}
              >
                <EyeIcon off={showPassword} />
              </button>
            )}
          />
          <p className="auth-hint-text">
            Must be at least 8 characters with an uppercase letter, a number, and a symbol.
          </p>

          <AuthField
            label="Confirm new password"
            icon={<LockIcon />}
            type={showPassword ? 'text' : 'password'}
            placeholder="Re-enter your new password"
            value={confirmPassword}
            onChange={e => {
              setConfirmPassword(e.target.value);
              clearFieldError('confirmPassword');
            }}
            error={fieldErrors.confirmPassword}
            required
          />

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? 'Saving…' : <>Reset password <ArrowRightIcon /></>}
          </button>
        </form>

        <div className="auth-resend-box">
          {!showResendInput && !resendEmail ? (
            <button
              type="button"
              className="auth-link-btn"
              onClick={() => setShowResendInput(true)}
            >
              Didn't receive a code? Resend code
            </button>
          ) : (
            <div style={{ marginTop: 6 }}>
              {resendError && <div className="auth-alert" style={{ marginBottom: 10 }}>{resendError}</div>}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Enter email or username"
                  value={resendEmail}
                  onChange={e => setResendEmail(e.target.value)}
                  style={{
                    flex: 1,
                    height: 40,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    background: 'rgba(255, 255, 255, 0.04)',
                    color: 'var(--text)',
                    fontSize: '0.85rem'
                  }}
                />
                <button
                  type="button"
                  className="auth-submit-btn"
                  onClick={handleResendCode}
                  disabled={resending || resendCountdown > 0 || !resendEmail.trim()}
                  style={{ width: 'auto', height: 40, padding: '0 14px', fontSize: '0.82rem', whiteSpace: 'nowrap' }}
                >
                  {resending
                    ? 'Sending…'
                    : resendCountdown > 0
                      ? `${resendCountdown}s`
                      : <>Resend <SendIcon /></>}
                </button>
              </div>
            </div>
          )}
        </div>

        <Link to="/login" className="auth-back-link">
          <BackArrowIcon /> Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}
